import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api-client";
import type { paths } from "@/lib/api-types";
import { adminQueryKeys } from "./queryKeys";
import {
  createListQueryInit,
  createDetailQueryInit,
  listQueryOptions,
  detailQueryOptions,
} from "./hooks";
import type { SortOrder } from "./sort";

export type AdminCheckoutListItem =
  paths["/api/admin/checkouts"]["get"]["responses"]["200"]["content"]["application/json"]["data"][number];

type AdminCheckoutsListQuery = {
  page: number;
  pageSize: number;
  sortField?: string | null;
  sortOrder: SortOrder;
  filter?: string;
};

export function useAdminCheckoutsList(params: AdminCheckoutsListQuery) {
  const init = createListQueryInit(params, "createdAt");
  return apiClient.useQuery(
    "get",
    "/api/admin/checkouts",
    init,
    listQueryOptions,
  );
}

export type AdminCheckoutDetail =
  paths["/api/admin/checkouts/{id}"]["get"]["responses"]["200"]["content"]["application/json"]["checkout"];

export function useAdminCheckout(checkoutId: string | undefined) {
  const init = createDetailQueryInit(checkoutId);
  return apiClient.useQuery(
    "get",
    "/api/admin/checkouts/{id}",
    init,
    detailQueryOptions(!!checkoutId),
  );
}

export function useAdminCheckoutApproveParticipants() {
  const queryClient = useQueryClient();
  return apiClient.useMutation(
    "post",
    "/api/admin/checkouts/{id}/approve-participants",
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.list,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.detail,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.participants.list,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.participants.detail,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.events.participantStats,
        });
      },
    },
  );
}

export function useAdminCheckoutTakeBalanceParticipants() {
  const queryClient = useQueryClient();
  return apiClient.useMutation(
    "post",
    "/api/admin/checkouts/{id}/take-balance-participants",
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.list,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.detail,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.participants.list,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.participants.detail,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.events.participantStats,
        });
      },
    },
  );
}

export function useAdminCheckoutPaymentLink() {
  return apiClient.useMutation("post", "/api/admin/checkouts/{id}/payment-link");
}

export function useAdminCheckoutApproveBoats() {
  const queryClient = useQueryClient();
  return apiClient.useMutation(
    "post",
    "/api/admin/checkouts/{id}/approve-boats",
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.list,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.detail,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.participants.list,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.participants.detail,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.events.participantStats,
        });
      },
    },
  );
}

export function useAdminCheckoutRefund() {
  const queryClient = useQueryClient();
  return apiClient.useMutation("post", "/api/admin/checkouts/{id}/refund", {
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.checkouts.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.checkouts.detail,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.list,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.participants.detail,
      });
      await queryClient.invalidateQueries({
        queryKey: adminQueryKeys.events.participantStats,
      });
    },
  });
}

export function useAdminCheckoutCreateUser() {
  const queryClient = useQueryClient();
  return apiClient.useMutation(
    "post",
    "/api/admin/checkouts/{id}/create-user",
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.list,
        });
        await queryClient.invalidateQueries({
          queryKey: adminQueryKeys.checkouts.detail,
        });
      },
    },
  );
}

